import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const useDialogA11y = ({ isOpen, containerRef, onClose, closeOnEscape = true }) => {
  const previousFocusRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!isOpen || !container) {
      return undefined;
    }

    previousFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    const focusTarget = container.querySelector(FOCUSABLE_SELECTOR) ?? container;

    const focusFrame = window.requestAnimationFrame(() => {
      focusTarget.focus?.();
    });

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && closeOnEscape) {
        event.stopPropagation();
        onClose?.();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));

      if (focusableElements.length === 0) {
        event.preventDefault();
        container.focus?.();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus?.();
    };
  }, [closeOnEscape, containerRef, isOpen, onClose]);
};

export default useDialogA11y;
